/**
 * Line icons.
 *
 * All icons share a 24-unit box, a 2-unit stroke, and `currentColor`, so they inherit size
 * and colour from their button and never need a separate colour token. Every icon is marked
 * `aria-hidden`; the control around it carries the accessible name.
 */

import type { ReactNode, SVGProps } from 'react';

/** Props accepted by every icon: standard SVG props plus a pixel size. */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /** Rendered width and height in pixels. */
  size?: number;
}

/** Shared SVG frame for every icon in the set. */
function Glyph({ size = 20, children, ...rest }: IconProps & { children: ReactNode }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Check mark; marks the attending state. */
export const CheckIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M4.5 12.5 9.5 17.5 19.5 6.5" /></Glyph>
);

/** Cross; marks the missing state. */
export const CrossIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M6 6 18 18M18 6 6 18" /></Glyph>
);

/** Note with a folded corner; marks the excused state, which always has a reason behind it. */
export const NoteIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </Glyph>
);

/** Clock reading a few minutes past; marks the late state. */
export const ClockIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3.5 2" />
  </Glyph>
);

/** Speech bubble; opens or marks a written note. */
export const CommentIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M20.5 12.5a7.5 7.5 0 0 1-7.5 7.5H8l-4.5 2.5V12.5A7.5 7.5 0 0 1 11 5h2a7.5 7.5 0 0 1 7.5 7.5z" />
  </Glyph>
);

/** Chevron pointing left; previous session. */
export const ChevronLeftIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M15 5 8 12l7 7" /></Glyph>
);

/** Chevron pointing right; next session. */
export const ChevronRightIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M9 5l7 7-7 7" /></Glyph>
);

/** Chevron pointing down; expands a disclosure. */
export const ChevronDownIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M5 9l7 7 7-7" /></Glyph>
);

/** Clipboard with a tick; the roll-call section. */
export const RollCallIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M9 4h6v3H9z" />
    <path d="M15 5.5h2A2 2 0 0 1 19 7.5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h2" />
    <path d="M8.5 13.5 10.5 15.5 15 11" />
  </Glyph>
);

/** Month grid; the calendar section. */
export const CalendarIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
  </Glyph>
);

/** Two figures; the roster section. */
export const PeopleIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M16 5.5a3.2 3.2 0 0 1 0 6M17 15.2c2.1.5 3.5 2.3 3.5 4.8" />
  </Glyph>
);

/** Ascending bars; the statistics section. */
export const ChartIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M4 20V13M10 20V7M16 20v-4M22 20H2" />
  </Glyph>
);

/** Sliders; project settings. */
export const SettingsIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2.2" />
    <circle cx="10" cy="17" r="2.2" />
  </Glyph>
);

/** Plus sign; creates a record. */
export const PlusIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}><path d="M12 5v14M5 12h14" /></Glyph>
);

/** Waste bin; deletes a record. */
export const TrashIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M4 7h16M10 7V4.5h4V7M6.5 7l1 13h9l1-13" />
  </Glyph>
);

/** Circling arrows; replication in progress. */
export const SyncIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M20 12a8 8 0 0 1-13.7 5.7M4 12a8 8 0 0 1 13.7-5.7" />
    <path d="M4 20v-4h4M20 4v4h-4" />
  </Glyph>
);

/** Struck-through signal; the device is offline. */
export const OfflineIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M3 3l18 18" />
    <path d="M6.5 11.5a8 8 0 0 1 4-2.2M2.5 8a13 13 0 0 1 5-3.2M16.5 9.4a8 8 0 0 1 1.2.8M14 5a13 13 0 0 1 7.5 3" />
    <path d="M9.5 15a4 4 0 0 1 5 0" />
    <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
  </Glyph>
);

/** Warning triangle; a failure that needs attention. */
export const AlertIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M12 4 2.5 20h19z" />
    <path d="M12 10v4.5M12 17.4v.2" />
  </Glyph>
);

/** Pencil; edits a record. */
export const PencilIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M4 20h4l10-10-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </Glyph>
);

/** Arrow leaving a door; sign out. */
export const SignOutIcon = (props: IconProps): ReactNode => (
  <Glyph {...props}>
    <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
    <path d="M16 8l4 4-4 4M20 12H9" />
  </Glyph>
);
